/**
 * Per-Speaker FR — light-themed Canvas2D paint layer for mobile's Graphs
 * tab. See `../perSpeaker.ts` for the geometry (a reshape of
 * `FreqResponseResponse.per_speaker`, no new backend data) and
 * `web/src/components/room/charts/perSpeakerDraw.ts` for the dark-dock
 * equivalent this mirrors the shape of.
 *
 * Speakers are told apart by a cycling dash pattern (`DASH_CYCLE`) plus a
 * small on-canvas legend, not colour — same "line style, not colour, tells
 * series apart" convention as every other multi-series chart in this dock.
 */
import { canvasTypeFont } from '@roomtreatment/design';
import {
  computePerSpeakerFreqResponseGeometry,
  type PerSpeakerInput,
  type PerSpeakerSeries,
} from '../perSpeaker';
import type { ChartPad, PlotFrame } from '../layout';
import { setupCanvas, drawEmptyMessage, drawLogFreqAxisGrid, drawSeriesLine, INK, DASH_CYCLE } from './_shared';

export const PER_SPEAKER_PAD: ChartPad = { t: 14, r: 14, b: 26, l: 40 };

function speakerLabel(s: { index: number; subkind: string | null }): string {
  return `${s.index + 1} · ${s.subkind ?? 'speaker'}`;
}

function drawLegend(ctx: CanvasRenderingContext2D, frame: PlotFrame, series: PerSpeakerSeries[]): void {
  ctx.font = canvasTypeFont('label');
  let lx = frame.plotX + 6;
  const ly = frame.plotY + 8;
  series.forEach((s, i) => {
    const label = speakerLabel(s);
    ctx.setLineDash(DASH_CYCLE[i % DASH_CYCLE.length]!);
    ctx.strokeStyle = INK.primary;
    ctx.lineWidth = i === 0 ? 2.0 : 1.4;
    ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(lx + 14, ly); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = INK.label;
    ctx.textAlign = 'left';
    ctx.fillText(label, lx + 17, ly + 3);
    lx += 17 + ctx.measureText(label).width + 10;
  });
}

export interface DrawPerSpeakerFreqResponseOpts {
  freqs: number[];
  speakers: PerSpeakerInput[];
  width: number;
  height: number;
  fMin?: number;
  fMax?: number;
}

export function drawPerSpeakerFreqResponseCanvas(canvas: HTMLCanvasElement, opts: DrawPerSpeakerFreqResponseOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computePerSpeakerFreqResponseGeometry({
    freqs: opts.freqs,
    speakers: opts.speakers,
    width: opts.width,
    height: opts.height,
    pad: PER_SPEAKER_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No per-speaker data yet');
    return;
  }

  drawLogFreqAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicks);
  geo.series.forEach((series, i) => {
    drawSeriesLine(ctx, geo.frame, series.points, INK.primary, i === 0 ? 2.0 : 1.4, DASH_CYCLE[i % DASH_CYCLE.length]);
  });
  drawLegend(ctx, geo.frame, geo.series);
}
